import { FormControl, FormGroup, Validators } from '@angular/forms';
import { HealthAnalysisJourneyStepEnum } from '../../module/health-analysis/journey/enum/health-analysis-journey-step.enum';
import { DocumentNumberValidator } from '../helper/document-number.validator';
import { MultipleEmailsValidator } from '../helper/multiple-emails.validator';
import { PasswordValidator } from '../helper/password.validator';
import { PrePostingServiceCode } from '../component/input/delivery-service-code/enum/service-code.enum';
import {
    DEFAULT_PARTNER_COLOR_VARIABLES,
    StylesheetBaseForms,
    StylesheetFoodForms,
    StylesheetGeneticForms,
    StylesheetMetabolomeForms,
    StylesheetMicrobiotaForms
} from './partner-stylesheet.const';

type formNames = 'PASSWORD'
    | 'USER_UPDATE'
    | 'BASE_CREATE'
    | 'BASE_IN_STOCK'
    | 'ADDRESS'
    | 'MAIL_PACKAGE'
    | 'DESTINATORY_DETAIL'
    | 'USER'
    | 'USER_BIRTH_PLACE'
    | 'PARTNER'
    | 'PARTER_UPDATE'

export const FORMS = (formName: HealthAnalysisJourneyStepEnum | formNames): FormGroup => {
    if (formName === HealthAnalysisJourneyStepEnum.SENT) {
        return new FormGroup({
            patientId: new FormControl(null, Validators.required),
            collectedAt: new FormControl(null, Validators.required)
        });
    } else if (formName === HealthAnalysisJourneyStepEnum.COLLECTED) {
        return new FormGroup({
            expectedDeliveryDate: new FormControl(null, Validators.required),
            receivedAt: new FormControl(null, Validators.required),
            sampleTemperature: new FormControl(null),
        });
    } else if (formName === HealthAnalysisJourneyStepEnum.RECEIVED) {
        return new FormGroup({
            expectedDeliveryDate: new FormControl(null, Validators.required),
            startedInLabAt: new FormControl(null, Validators.required),
        });
    } else if (formName === HealthAnalysisJourneyStepEnum.PROCESSING_IN_LABORATORY) {
        return new FormGroup({
            processedInLabAt: new FormControl(null, Validators.required),
            expectedDeliveryDate: new FormControl(null, Validators.required)
        });
    } else if (formName === 'BASE_CREATE'){
        return new FormGroup({
            prescriberId: new FormControl(null),
            patientId: new FormControl(null),
            alreadySent: new FormControl(false),
            healthAnalysisTypeId: new FormControl(null, Validators.required),
            partnerId: new FormControl(null, Validators.required),
            sampleType: new FormControl(null, Validators.required),
            quantity: new FormControl(1, [Validators.min(1), Validators.required])
        });
    } else if (formName === 'BASE_IN_STOCK'){
        return new FormGroup({
            prescriberId: new FormControl(null, Validators.required),
            patientId: new FormControl(null),
            sentAt: new FormControl(null, Validators.required),
        });
    } else if (formName === 'ADDRESS') {
        return new FormGroup({
            zipCode: new FormControl(null, [Validators.required, Validators.minLength(8), Validators.maxLength(8)]),
            streetName: new FormControl(null, Validators.required),
            number: new FormControl(null, Validators.required),
            city: new FormControl({value: null, disabled: true}, Validators.required),
            state: new FormControl({value: null, disabled: true}, Validators.required),
            neighborhood: new FormControl(null, Validators.required),
            country: new FormControl('Brasil', Validators.required),
            complement: new FormControl(null),
            trackingCode: new FormControl('')
        });
    } else if (formName === 'MAIL_PACKAGE') {
        return new FormGroup({
            height: new FormControl('28', Validators.required),
            width: new FormControl('19', Validators.required),
            length: new FormControl('19', Validators.required),
            weight: new FormControl('500', Validators.required),
            serviceCode: new FormControl(PrePostingServiceCode.SEDEX, Validators.required), // Default value is 'SEDEX'
        });
    } else if (formName === 'DESTINATORY_DETAIL') {
        return new FormGroup({
            name: new FormControl(null, Validators.required),
            documentNumber: new FormControl(null, [
                Validators.pattern('^([0-9]{11}|[0-9]{14})$'),
                Validators.required
            ])
        });
    } else if (formName === 'USER') {
        return new FormGroup({
            name: new FormControl('', [Validators.required]),
            documentNumber: new FormControl('', [
                Validators.required,
                DocumentNumberValidator.validateDocumentNumber
            ]),
            documentType: new FormControl('CPF', Validators.required),
            language: new FormControl('pt-BR', Validators.required),
            email: new FormControl('', Validators.email),
            secondaryEmails: new FormControl([], MultipleEmailsValidator.validate),
            biologicalGender: new FormControl('', Validators.required),
            birthDate: new FormControl(null),
            height: new FormControl(null),
            weight: new FormControl(null)
        });
    } else if (formName === 'USER_BIRTH_PLACE') {
        return new FormGroup({
            city: new FormControl(''),
            state: new FormControl(''),
            country: new FormControl('BR', Validators.required)
        });
    } else if (formName === 'PASSWORD') {
        return new FormGroup({
            actualPassword: new FormControl(null, Validators.required),
            newPassword: new FormControl('', [ Validators.required, PasswordValidator.complexity]),
            newPasswordConfirmation: new FormControl(null, [Validators.required, PasswordValidator.mustBeEquals])
        });
    } else if (formName === 'USER_UPDATE') {
        return new FormGroup({
            name: new FormControl('', [Validators.required]),
            email: new FormControl('', Validators.email),
            secondaryEmails: new FormControl([], MultipleEmailsValidator.validate),
            biologicalGender: new FormControl('', Validators.required),
            birthDate: new FormControl(null),
            height: new FormControl(null),
            weight: new FormControl(null),
            documentNumber: new FormControl({
                value: '',
                disabled: true
            }),
        });
    } else if (formName === 'PARTNER') {
        return new FormGroup({
            name: new FormControl('', [Validators.required]),
            fantasyName: new FormControl(''),
            email: new FormControl('', Validators.email),
            secondaryEmails: new FormControl([], MultipleEmailsValidator.validate),
            defaultRules: new FormControl([]),
            documentNumber: new FormControl('', Validators.required),
            documentType: new FormControl('', Validators.required),
            language: new FormControl('pt-BR', Validators.required),
            logo: new FormControl('', [Validators.required]),
            stylesheet: new FormGroup({
                base: StylesheetBaseForms(DEFAULT_PARTNER_COLOR_VARIABLES.base),
                food: StylesheetFoodForms(DEFAULT_PARTNER_COLOR_VARIABLES.food),
                genetic: StylesheetGeneticForms(DEFAULT_PARTNER_COLOR_VARIABLES.genetic),
                metabolome: StylesheetMetabolomeForms(DEFAULT_PARTNER_COLOR_VARIABLES.metabolome),
                microbiota: StylesheetMicrobiotaForms(DEFAULT_PARTNER_COLOR_VARIABLES.microbiota),
            }),
            cmvs: new FormControl('', [Validators.required]),
            cnes: new FormControl('', [Validators.required]),
            technicalManagerRegisterType: new FormControl('', [Validators.required]),
            technicalManagerRegisterValue: new FormControl('', [Validators.required]),
            technicalManagerRegisterState: new FormControl('', [Validators.required]),
            technicalManagerId: new FormControl('', [Validators.required]),
        });
    } else if (formName === 'PARTER_UPDATE') {
        return new FormGroup({
            name: new FormControl(''),
            fantasyName: new FormControl(''),
            email: new FormControl('', Validators.email),
            secondaryEmails: new FormControl([], MultipleEmailsValidator.validate),
            defaultRules: new FormControl([]),
            logo: new FormControl(''),
            stylesheet: new FormGroup({
                base: StylesheetBaseForms(),
                food: StylesheetFoodForms(),
                genetic: StylesheetGeneticForms(),
                metabolome: StylesheetMetabolomeForms(),
                microbiota: StylesheetMicrobiotaForms(),
            }),
            documentNumber: new FormControl({ value: '', disabled: true }),
            documentType: new FormControl({ value: '', disabled: true }),
            language: new FormControl(''),
            cmvs: new FormControl(''),
            cnes: new FormControl(''),
            technicalManagerRegisterType: new FormControl('', [Validators.required]),
            technicalManagerRegisterValue: new FormControl('', [Validators.required]),
            technicalManagerRegisterState: new FormControl('', [Validators.required]),
            technicalManagerId: new FormControl(''),
        });
    } else {
        throw new Error('Form Not Found');
    }
}
